import {
  isNil,
  sumBy,
  maxBy,
  flow,
  map,
  filter,
  get,
  pick,
  reduce,
  add,
  negate,
  sum,
} from 'lodash/fp';
import {
  EnhancedPlan,
  PlansAllotments,
} from '@mm/state/interfaces/subscription';
import { CouponObject } from '@mm/state/controllers/SubscriptionsController';
import { centsToDollars } from '@mm/utils';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import { getPlanPrice } from '@mm/state/helpers/get-plan-price';

// tslint:disable-next-line: cyclomatic-complexity
export const getPriceByTeamPlan = ({
  plan,
  allPlansAllotments,
  coupon,
  futurePaymentCredits,
  subscriptions,
}: {
  plan: EnhancedPlan;
  allPlansAllotments: PlansAllotments;
  coupon: CouponObject | undefined;
  futurePaymentCredits?: number;
  subscriptions?: SubscriptionModel[];
}) => {
  const subscriptionData = map(
    pick([
      'unitAmountInCents',
      'userSeats',
      'extraUserSeats',
      'smsNumbers',
      'priceFactor',
      'basePrice',
    ]),
  )(subscriptions);
  const subscriptionProRatedAmount = centsToDollars(
    flow([
      map(pick(['basePrice', 'priceFactor'])),
      reduce(
        (
          acc,
          {
            basePrice,
            priceFactor,
          }: { basePrice: number; priceFactor: number },
        ) => add(acc, basePrice * priceFactor),
        0,
      ),
    ])(subscriptionData),
  );
  const subscriptionPrices = sumBy('unitAmountInCents')(subscriptionData);
  const {
    userSeats: userSeatsPicked,
  }: {
    userSeats?: number | undefined | null;
  } = pick(
    ['extraUserSeats', 'userSeats'],
    maxBy('userSeats', subscriptionData),
  );

  const userSeats = userSeatsPicked || -1;

  const smsNumberCount = flow([
    map(get('smsNumbers')),
    filter(negate(isNil)),
    map(reduce((acc, value) => (value ? add(acc)(1) : acc), 0)),
    sum,
  ])(subscriptionData);

  const currentSmsNumberPrice = smsNumberCount * plan.smsNumberAddOnPrice;

  const additionalUserSeats = userSeats - plan.userSeats;
  const newUserSeatsValue =
    additionalUserSeats > 0 ? userSeats : plan.userSeats;
  const newUserSeatsPrice =
    additionalUserSeats > 0
      ? additionalUserSeats * plan.userSeatsAddOnPrice
      : 0;

  const newSendCreditsValue = allPlansAllotments[plan.planCode]
    ? allPlansAllotments[plan.planCode].start
    : plan.outgoingMessagesLimit || -1;

  const currentMonthlyFee = Number(centsToDollars(subscriptionPrices)) || 0;

  const planPrice = plan.unitAmountInCents;

  const newMonthlyFee =
    Number(centsToDollars(getPlanPrice(planPrice, coupon))) + newUserSeatsPrice;

  const payNow = Number(centsToDollars(getPlanPrice(planPrice, coupon)));

  return {
    planPrice: planPrice || null,
    newSendCreditsValue: null,
    currentSendCreditsValue: newSendCreditsValue,
    currentMonthlyFee,
    currentUserSeatsValue: subscriptionData.length ? userSeats : 0,
    currentUserSeatsPrice: null,
    newUserSeatsPrice,
    newUserSeatsValue,
    currentSendCreditsPrice: null,
    newSendCreditsPrice: 0,
    newMonthlyFee,
    payNow,
    proRatedAmount: futurePaymentCredits ? 1 : null,
    proRatedAsCredit: futurePaymentCredits,
    smsNumberCount,
    newSmsNumberCount: null,
    newSmsNumberPrice: currentSmsNumberPrice,
    currentSmsNumberPrice: null,
    showAddCoupon: true,
  };
};
